import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, fmtDate, money } from "@/lib/api";
import { PageHead } from "./Layout";
import { qtyText, stageOf } from "./Leads";

export const priceText = (l: any) => {
  if (l.final_price_cents) return `${money(l.final_price_cents)} (adjusted)`;
  if (l.estimate_cents == null) return "Not priced yet";
  return l.low_cents === l.high_cents ? money(l.estimate_cents) : `${money(l.low_cents)} – ${money(l.high_cents)}`;
};

export default function FieldQuotes() {
  const [items, setItems] = useState<any[] | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => { api.get("/orgs/me/leads", { params: { source: "field_quote", page_size: 100 } }).then((r) => setItems(r.data.items)).catch(() => setFailed(true)); }, []);
  return (
    <div className="fade-up">
      <PageHead title="Field Quotes" sub="Measure a property and price a job on site, using the same satellite measuring and pricing as your website widget."
        actions={<Button asChild size="lg" className="h-12 w-full sm:w-auto" data-testid="new-field-quote-button"><Link to="/app/field-quotes/new"><Plus className="h-5 w-5 mr-1" />New Field Quote</Link></Button>} />
      {!items && !failed && <p className="text-sm text-muted-foreground" data-testid="field-quotes-loading">Loading…</p>}
      {failed && <p className="text-sm text-destructive" role="alert" data-testid="field-quotes-error">Couldn’t load field quotes. Check your connection and refresh.</p>}
      {items && items.length === 0 && (
        <div className="bg-card border rounded-xl p-10 text-center" data-testid="field-quotes-empty">
          <p className="font-medium">No field quotes yet</p>
          <p className="text-sm text-muted-foreground mt-1">Start one while you’re at the customer’s property.</p>
        </div>
      )}
      {!!items?.length && <ul className="space-y-3" data-testid="field-quotes-list">
        {items?.map((l) => (
          <li key={l.id}>
            <Link to={`/app/field-quotes/${l.id}`} className="block bg-card border rounded-xl p-4 hover:border-primary transition-colors" data-testid={`field-quote-item-${l.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold truncate">{l.name}</p>
                  <p className="text-sm text-muted-foreground flex items-center gap-1 truncate"><MapPin className="h-3.5 w-3.5 shrink-0" />{l.confirmed_service_address}</p>
                  <p className="text-xs text-muted-foreground mt-1">{l.service_label} · {qtyText(l)} · {fmtDate(l.created_at)}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-mono font-semibold text-sm">{priceText(l)}</p>
                  <span className={`inline-block mt-1 text-[11px] rounded-full px-2 py-0.5 ${stageOf(l).cls}`}>{stageOf(l).label}</span>
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>}
    </div>
  );
}
